/**
 * Scrolling pip row for a carousel.
 *
 * A fixed number of pips is shown at a time and the rail keeps the active one
 * centred, so a gallery of any length gets the same compact row. The pips out
 * of view are reachable by dragging the rail, and each one jumps to its slide.
 *
 * Pips are built here rather than in Liquid because how many a carousel needs
 * depends on where it can snap, which isn't settled until it has laid out:
 *
 *   const pips = el.querySelector("carousel-pips");
 *   pips.count = 12;
 *   pips.active = 3;
 *   pips.addEventListener("carousel-pips:select", (e) => e.detail.index);
 *
 * `window.connectCarouselPips(swiper, pips)` wires all of that to a Swiper
 * instance and returns a teardown function.
 */

// How far a pointer travels before the press counts as a drag of the rail
// rather than a tap on a pip.
const DRAG_THRESHOLD = 4;

// Below this there is nothing to page through, so the row hides itself.
const MIN_COUNT = 2;

// Placeholder in `data-pip-label`, standing in for the pip's slide number.
const LABEL_TOKEN = "[index]";

const scrollBehavior = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";

class CarouselPips extends HTMLElement {
  constructor() {
    super();

    this.rail = null;
    this.pips = [];
    this._count = 0;
    this._active = 0;
    // The live pointer interaction, promoted to a drag once the pointer has
    // travelled far enough.
    this.press = null;
    // The pip that press started on, consumed by the click it goes on to make.
    this.pressedPip = null;
    this.dragged = false;
    this.scrollFrame = null;

    this.handlePointerDown = (e) => {
      if (!e.isPrimary) return;

      this.dragged = false;
      // The rail can scroll between press and release: focus on a half-hidden
      // pip has the browser scroll it into view, which slides it out from under
      // the pointer and retargets the click at the rail. So the press decides
      // which pip was meant, not the click.
      this.pressedPip = e.target.closest("[data-index]");
      this.press = {
        id: e.pointerId,
        x: e.clientX,
        scroll: this.rail.scrollLeft,
        // Touch and trackpad panning is the browser's job; a mouse gets none,
        // so its drags are followed by hand.
        draggable: e.pointerType !== "touch",
        dragging: false,
      };
    };

    this.handlePointerMove = (e) => {
      const { press } = this;
      if (!press || !press.draggable || e.pointerId !== press.id) return;

      const distance = e.clientX - press.x;
      if (!press.dragging && Math.abs(distance) < DRAG_THRESHOLD) return;

      if (!press.dragging) {
        press.dragging = true;
        // Keeps the events coming once the pointer leaves the 24px-tall rail.
        this.rail.setPointerCapture(e.pointerId);
        this.rail.toggleAttribute("data-dragging", true);
      }

      this.rail.scrollLeft = press.scroll - distance;
    };

    this.handlePointerUp = (e) => {
      if (!this.press || e.pointerId !== this.press.id) return;

      // A drag that moved has to swallow the click it lands on — the pip under
      // the pointer is only wherever the drag happened to end.
      this.dragged = this.press.dragging;
      if (this.dragged) this.pressedPip = null;

      this.rail.removeAttribute("data-dragging");
      this.press = null;
    };

    this.handleClick = (e) => {
      // Nothing pressed means the keyboard activated the pip.
      const pip = this.pressedPip || e.target.closest("[data-index]");
      this.pressedPip = null;

      if (this.dragged) {
        this.dragged = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (!pip) return;

      this.dispatchEvent(
        new CustomEvent("carousel-pips:select", {
          bubbles: true,
          detail: { index: Number(pip.dataset.index) },
        }),
      );
    };

    // Tabbing to a pip out of view scrolls it flush against the edge; centring
    // it keeps its neighbours visible too.
    this.handleFocusIn = (e) => this.center(e.target.closest("[data-index]"));

    this.handleScroll = () => {
      if (this.scrollFrame) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateEdges();
      });
    };
  }

  connectedCallback() {
    if (!this.rail) this.buildRail();

    this.rail.addEventListener("pointerdown", this.handlePointerDown);
    this.rail.addEventListener("pointermove", this.handlePointerMove);
    this.rail.addEventListener("pointerup", this.handlePointerUp);
    this.rail.addEventListener("pointercancel", this.handlePointerUp);
    this.rail.addEventListener("click", this.handleClick, true);
    this.rail.addEventListener("focusin", this.handleFocusIn);
    this.rail.addEventListener("scroll", this.handleScroll, { passive: true });

    // How many pips fit is a width away from changing at any breakpoint.
    this.observer = new ResizeObserver(() => {
      this.updateEdges();
      this.center(this.pips[this._active]);
    });
    this.observer.observe(this.rail);

    // A consumer can set `count` before the element lands in the DOM, so the
    // pips are built here rather than from the setter alone.
    this.render();
  }

  disconnectedCallback() {
    this.rail.removeEventListener("pointerdown", this.handlePointerDown);
    this.rail.removeEventListener("pointermove", this.handlePointerMove);
    this.rail.removeEventListener("pointerup", this.handlePointerUp);
    this.rail.removeEventListener("pointercancel", this.handlePointerUp);
    this.rail.removeEventListener("click", this.handleClick, true);
    this.rail.removeEventListener("focusin", this.handleFocusIn);
    this.rail.removeEventListener("scroll", this.handleScroll);

    this.observer.disconnect();
  }

  set count(value) {
    const count = Math.max(0, Math.floor(value) || 0);
    if (count === this._count) return;

    this._count = count;
    this.render();
  }

  get count() {
    return this._count;
  }

  set active(value) {
    const last = Math.max(0, this._count - 1);
    this._active = Math.min(Math.max(0, Math.floor(value) || 0), last);

    this.paint();
    this.center(this.pips[this._active]);
  }

  get active() {
    return this._active;
  }

  buildRail() {
    this.rail = document.createElement("div");
    this.rail.className = "carousel-pips__rail";
    this.rail.setAttribute("role", "group");

    if (this.dataset.railLabel) {
      this.rail.setAttribute("aria-label", this.dataset.railLabel);
    }

    this.appendChild(this.rail);
  }

  render() {
    if (!this.rail) return;

    this.hidden = this._count < MIN_COUNT;

    const label = this.dataset.pipLabel;
    const fragment = document.createDocumentFragment();

    this.pips = Array.from({ length: this._count }, (_, index) => {
      const pip = document.createElement("button");
      pip.type = "button";
      pip.className = "carousel-pips__pip";
      pip.dataset.index = index;
      pip.setAttribute(
        "aria-label",
        label ? label.replace(LABEL_TOKEN, index + 1) : `${index + 1}`,
      );

      const dot = document.createElement("span");
      dot.className = "carousel-pips__pip-dot";
      pip.appendChild(dot);

      fragment.appendChild(pip);
      return pip;
    });

    this.rail.replaceChildren(fragment);
    this.paint();
    this.updateEdges();
  }

  paint() {
    this.pips.forEach((pip, index) => {
      if (index === this._active) {
        pip.setAttribute("aria-current", "true");
      } else {
        pip.removeAttribute("aria-current");
      }
    });
  }

  /**
   * Slides the rail so `pip` sits in the middle of the window. `scrollTo`
   * clamps for us, so the first and last few pips settle against an end
   * instead of leaving dead space beside them.
   */
  center(pip) {
    // While a pointer is down the rail belongs to it — centring mid-press only
    // fights the drag, or shifts the pip being tapped.
    if (!pip || this.press) return;

    this.rail.scrollTo({
      left: pip.offsetLeft + pip.offsetWidth / 2 - this.rail.clientWidth / 2,
      behavior: scrollBehavior(),
    });
  }

  /**
   * Flags which ends still have pips out of view, so only those edges fade.
   */
  updateEdges() {
    const { scrollLeft, scrollWidth, clientWidth } = this.rail;
    // Fractional scroll positions never quite land on either end.
    const end = scrollWidth - clientWidth - 1;

    this.toggleAttribute("data-overflow-start", scrollLeft > 1);
    this.toggleAttribute("data-overflow-end", scrollLeft < end);
  }
}

if (!customElements.get("carousel-pips")) {
  customElements.define("carousel-pips", CarouselPips);
}

// Swiper events that move or resize the snap points behind the pips.
const SWIPER_EVENTS = [
  "init",
  "snapIndexChange",
  "activeIndexChange",
  "snapGridLengthChange",
  "slidesLengthChange",
  "observerUpdate",
  "resize",
].join(" ");

/**
 * Drives a `<carousel-pips>` from a Swiper instance.
 *
 * Pips map to Swiper's snap points rather than its slides, matching Swiper's
 * own pagination — a carousel running `slidesPerView: "auto"` gets one pip per
 * position it can rest at.
 *
 * @param {Swiper} swiper
 * @param {CarouselPips} pips
 * @returns {Function} teardown, to call alongside `swiper.destroy()`
 */
window.connectCarouselPips = (swiper, pips) => {
  if (!swiper || !pips) return () => {};

  const sync = () => {
    const { loop, slidesPerGroup } = swiper.params;

    pips.count = loop
      ? Math.ceil(swiper.slides.length / slidesPerGroup)
      : swiper.snapGrid.length;
    pips.active = loop
      ? Math.floor(swiper.realIndex / slidesPerGroup)
      : swiper.snapIndex;
  };

  const select = (e) => {
    const { loop, slidesPerGroup } = swiper.params;
    const index = e.detail.index * slidesPerGroup;

    if (loop) {
      swiper.slideToLoop(index);
    } else {
      swiper.slideTo(index);
    }
  };

  swiper.on(SWIPER_EVENTS, sync);
  pips.addEventListener("carousel-pips:select", select);
  sync();

  return () => {
    swiper.off(SWIPER_EVENTS, sync);
    pips.removeEventListener("carousel-pips:select", select);
  };
};
